import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';

export interface IconMapping {
  [key: string]:
    | {
        mainIcon: IconName;
        mainIcon_transform?: string;
        mainIcon_style?: IconPrefix;
        additionalIcon?: IconName;
        additionalIcon_transform?: string;
        additionalIcon_style?: IconPrefix;
        text?: string;
        text_transform?: string;
        type?: 'index';
      }
    | undefined;
}

export enum IconType {
  MAIN_ICON,
  MAIN_ICON_ADDITIONAL_ICON,
  MAIN_ICON_TEXT,
  ADDITIONAL_ICON,
  UNDEFINED_ICON,
}

export const iconTransforms = {
  top_right: 'shrink-5 up-6 right-14',
  top_right_extraspace: 'shrink-5 up-6 right-16',
  bottom_right: 'shrink-5 down-1 right-12',
};

export const textTransforms = {
  text_center_right: 'shrink-6 right-12',
  text_center_right_extraspace: 'shrink-6 right-16',
};

export const iconMappings: IconMapping = {
  valid_at: { mainIcon: 'timer' },
  tt: { mainIcon: 'thermometer-half' },
  hcl: { mainIcon: 'cloud-fog' },
  tt_F: { mainIcon: 'thermometer-half' },
  tx: { mainIcon: 'thermometer-full' },
  tx_F: { mainIcon: 'thermometer-full' },
  tn: { mainIcon: 'thermometer-empty' },
  tn_F: { mainIcon: 'thermometer-empty' },
  gmaAlarm: { mainIcon: 'exclamation-triangle' },
  td: { mainIcon: 'dewpoint' },
  td_F: { mainIcon: 'dewpoint' },
  n: { mainIcon: 'cloud' },
  sun: {
    mainIcon: 'sun',
    additionalIcon: 'timer',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  ne: { mainIcon: 'cloud' },
  ne_symbol: { mainIcon: 'cloud' },
  nl: { mainIcon: 'cloud-download-alt' },
  nl_symbol: { mainIcon: 'cloud-arrow-down' },
  nlow: { mainIcon: 'cloud-download-alt' },
  nm: { mainIcon: 'cloud-upload-alt' },
  nm_symbol: { mainIcon: 'cloud-minus' },
  nh_symbol: { mainIcon: 'cloud-arrow-up' },
  ss1: { mainIcon: 'sunglasses' },
  ff: { mainIcon: 'wind' },
  ff_mph: { mainIcon: 'wind' },
  wind: { mainIcon: 'wind' },
  fg: { mainIcon: 'windsock' },
  ffg: { mainIcon: 'windsock' },
  fx: { mainIcon: 'windsock' },
  fx_mph: { mainIcon: 'windsock' },
  ffx: { mainIcon: 'windsock' },
  ffx_mph: { mainIcon: 'windsock' },
  dd: { mainIcon: 'location-arrow' },
  dd_degree: { mainIcon: 'compass' },
  ddg: {
    mainIcon: 'location-arrow',
    additionalIcon: 'windsock',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  webcam: { mainIcon: 'video' },
  salt: { mainIcon: 'mountain' },
  salt2: { mainIcon: 'mountain' },
  vis: { mainIcon: 'binoculars' },
  vv: { mainIcon: 'binoculars' },
  road_tt: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  road_tt2: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  road_tt3: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  road: { mainIcon: 'road', mainIcon_style: 'far' },
  road_condition: { mainIcon: 'road', mainIcon_style: 'far' },
  road_condition2: { mainIcon: 'road', mainIcon_style: 'far' },
  road_condition3: { mainIcon: 'road', mainIcon_style: 'far' },
  road_rsc: { mainIcon: 'road', mainIcon_style: 'far' },
  road_rst: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  road_rst_F: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  road_rst_max: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  road_rst_max_F: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  road_rst_min: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  road_rst_min_F: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  bike_rsc: { mainIcon: 'biking' },
  bike_rst: {
    mainIcon: 'biking',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bike_rst_F: {
    mainIcon: 'biking',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bike_rst_max: {
    mainIcon: 'biking',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bike_rst_max_F: {
    mainIcon: 'biking',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bike_rst_min: {
    mainIcon: 'biking',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  bike_rst_min_F: {
    mainIcon: 'biking',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  foot_rsc: { mainIcon: 'walking' },
  foot_rst: {
    mainIcon: 'walking',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  foot_rst_F: {
    mainIcon: 'walking',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  foot_rst_max: {
    mainIcon: 'walking',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  foot_rst_max_F: {
    mainIcon: 'walking',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  foot_rst_min: {
    mainIcon: 'walking',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  foot_rst_min_F: {
    mainIcon: 'walking',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  bridge_rsc: { mainIcon: 'archway' },
  bridge_rst: {
    mainIcon: 'archway',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bridge_rst_F: {
    mainIcon: 'archway',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bridge_rst_max: {
    mainIcon: 'archway',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bridge_rst_max_F: {
    mainIcon: 'archway',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  bridge_rst_min: {
    mainIcon: 'archway',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  bridge_rst_min_F: {
    mainIcon: 'archway',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['bottom_right'],
  },
  road_rh: {
    mainIcon: 'road',
    mainIcon_style: 'far',
    additionalIcon: 'humidity',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  // without minus
  t5: {
    mainIcon: 'thermometer-half',
    text: '5',
    text_transform: textTransforms['text_center_right'],
  },
  tg0: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '0',
    text_transform: textTransforms['text_center_right'],
  },
  tg5: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '5',
    text_transform: textTransforms['text_center_right'],
  },
  tg5_2: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '5',
    text_transform: textTransforms['text_center_right'],
  },
  tg_5cm: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '5',
    text_transform: textTransforms['text_center_right'],
  },
  tg_5cm_F: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '2',
    text_transform: textTransforms['text_center_right'],
  },
  tg10: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '10',
    text_transform: textTransforms['text_center_right'],
  },
  tg_10cm: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '10',
    text_transform: textTransforms['text_center_right'],
  },
  tg_10cm_F: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '4',
    text_transform: textTransforms['text_center_right'],
  },
  tg20: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '20',
    text_transform: textTransforms['text_center_right'],
  },
  tg_20cm: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '20',
    text_transform: textTransforms['text_center_right'],
  },
  tg_20cm_F: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '8',
    text_transform: textTransforms['text_center_right'],
  },
  tg30: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '30',
    text_transform: textTransforms['text_center_right'],
  },
  tg_30cm: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '30',
    text_transform: textTransforms['text_center_right'],
  },
  tg_30cm_F: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '30',
    text_transform: textTransforms['text_center_right'],
  },
  tg50: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '50',
    text_transform: textTransforms['text_center_right'],
  },
  tg_50cm: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '50',
    text_transform: textTransforms['text_center_right'],
  },
  tg_50cm_F: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '20',
    text_transform: textTransforms['text_center_right'],
  },
  tg100: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '100',
    text_transform: textTransforms['text_center_right'],
  },
  tg_100cm: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '100',
    text_transform: textTransforms['text_center_right'],
  },
  tg_100cm_F: {
    mainIcon: 'thermometer-half',
    additionalIcon: 'minus',
    additionalIcon_transform: iconTransforms['top_right'],
    text: '40',
    text_transform: textTransforms['text_center_right'],
  },
  wft: { mainIcon: 'water' },
  ww: { mainIcon: 'umbrella' },
  r_type: { mainIcon: 'umbrella' },
  r_intensity: { mainIcon: 'raindrops' },
  r_avail: { mainIcon: 'tint' },
  rh: { mainIcon: 'humidity' },
  road_grip: { mainIcon: 'tire' },
  freezing_tt: { mainIcon: 'temperature-frigid' },
  snow_height: {
    mainIcon: 'snowflake',
    additionalIcon: 'ruler-vertical',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  sn: {
    mainIcon: 'snowflake',
    additionalIcon: 'ruler-vertical',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  sn_in: {
    mainIcon: 'snowflake',
    additionalIcon: 'ruler-vertical',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  ppp: {
    mainIcon: 'stopwatch',
    text: 'NN',
    text_transform: iconTransforms['top_right_extraspace'],
  },
  qfe: {
    mainIcon: 'stopwatch',
    text: 'Stat',
    text_transform: iconTransforms['top_right_extraspace'],
  },
  rr: { mainIcon: 'flask' },
  rr_in: { mainIcon: 'flask' },
  warn: { mainIcon: 'exclamation-triangle' },
  rst: { mainIcon: 'thermometer-half' },
  rsc: { mainIcon: 'road', mainIcon_style: 'far' },
  wws: {
    mainIcon: 'cloud-hail',
    additionalIcon: 'percent',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wwp: {
    mainIcon: 'cloud-rain',
    additionalIcon: 'percent',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wwc: {
    mainIcon: 'cloud-showers',
    additionalIcon: 'percent',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wwt: {
    mainIcon: 'bolt',
    additionalIcon: 'percent',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  tt_950hp: {
    mainIcon: 'thermometer-half',
    text: '950',
    text_transform: textTransforms['text_center_right_extraspace'],
  },
  tt_950hp_F: {
    mainIcon: 'thermometer-half',
    text: '950',
    text_transform: textTransforms['text_center_right_extraspace'],
  },
  dd_950: {
    mainIcon: 'location-arrow',
    text: '950',
    text_transform: textTransforms['text_center_right_extraspace'],
  },
  ff_950: {
    mainIcon: 'wind',
    text: '950',
    text_transform: textTransforms['text_center_right_extraspace'],
  },
  ff_950_mph: {
    mainIcon: 'wind',
    text: '950',
    text_transform: textTransforms['text_center_right_extraspace'],
  },
  snlv: { mainIcon: 'mountains' },
  snlv_ft: { mainIcon: 'mountains' },
  symbol: { mainIcon: 'clouds-sun' },
  i_gm: {
    type: 'index',
    mainIcon: 'flower-daffodil',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_he: {
    type: 'index',
    mainIcon: 'temperature-hot',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_co: {
    type: 'index',
    mainIcon: 'temperature-frigid',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_hr: {
    type: 'index',
    mainIcon: 'cloud-showers-heavy',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_lr: {
    type: 'index',
    mainIcon: 'cloud-showers',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_st: {
    type: 'index',
    mainIcon: 'windsock',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_th: {
    type: 'index',
    mainIcon: 'bolt',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_uv: {
    type: 'index',
    mainIcon: 'sun',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_ld: {
    type: 'index',
    mainIcon: 'leaf',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_sl: {
    type: 'index',
    mainIcon: 'snowflake',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_di: {
    type: 'index',
    mainIcon: 'plane',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  wing_rst: {
    mainIcon: 'plane',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wing_rst_F: {
    mainIcon: 'plane',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wing_rst_max: {
    mainIcon: 'plane',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wing_rst_max_F: {
    mainIcon: 'plane',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wing_rst_min: {
    mainIcon: 'plane',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wing_rst_min_F: {
    mainIcon: 'plane',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  wing_rsc: {
    mainIcon: 'plane',
    additionalIcon: 'cloud-sleet',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  i_sl2: {
    mainIcon: 'train',
    mainIcon_transform: 'rotate-30',
    additionalIcon: 'exclamation-triangle',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  rail_tt: {
    mainIcon: 'train',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  rail_tt_F: {
    mainIcon: 'train',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  rail_tn: {
    mainIcon: 'train',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  rail_tn_F: {
    mainIcon: 'train',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  rail_tx: {
    mainIcon: 'train',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  rail_tx_F: {
    mainIcon: 'train',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right'],
  },
  rail_condition: {
    mainIcon: 'train',
    additionalIcon: 'cloud-sleet',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  overhead_tt: {
    mainIcon: 'broadcast-tower',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  overhead_tt_F: {
    mainIcon: 'broadcast-tower',
    additionalIcon: 'thermometer-half',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  overhead_tn: {
    mainIcon: 'broadcast-tower',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  overhead_tn_F: {
    mainIcon: 'broadcast-tower',
    additionalIcon: 'thermometer-empty',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  overhead_tx: {
    mainIcon: 'broadcast-tower',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  overhead_tx_F: {
    mainIcon: 'broadcast-tower',
    additionalIcon: 'thermometer-full',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  overhead_condition: {
    mainIcon: 'broadcast-tower',
    additionalIcon: 'cloud-sleet',
    additionalIcon_transform: iconTransforms['top_right_extraspace'],
  },
  i_pi: {
    type: 'index',
    mainIcon: 'dove',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_ff_pi: {
    type: 'index',
    mainIcon: 'wind',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_hfo_pi: {
    type: 'index',
    mainIcon: 'grip-lines',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_ne_pi: {
    type: 'index',
    mainIcon: 'clouds',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_nl_pi: {
    type: 'index',
    mainIcon: 'cloud-download-alt',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_nm_pi: {
    type: 'index',
    mainIcon: 'cloud-upload-alt',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_rr_pi: {
    type: 'index',
    mainIcon: 'cloud-rain',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_th_pi: {
    type: 'index',
    mainIcon: 'cloud-bolt',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_iv_pi: {
    type: 'index',
    mainIcon: 'layer-group',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_co_pi: {
    type: 'index',
    mainIcon: 'temperature-frigid',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_he_pi: {
    type: 'index',
    mainIcon: 'temperature-hot',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_fo_pi: {
    type: 'index',
    mainIcon: 'binoculars',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_hu_pi: {
    type: 'index',
    mainIcon: 'humidity',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_gp: {
    type: 'index',
    mainIcon: 'sprinkler',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
  i_gc: {
    type: 'index',
    mainIcon: 'sprinkler',
    additionalIcon: 'square',
    additionalIcon_style: 'fal',
  },
};
